/**
 * FutureResidentsClub — Weidner Classic
 *
 * Standard Weidner Future Residents Club layout, shared with the Belmont,
 * Oasis, Vantage, Blue Haven, Highland, and Via6 Weidner themes (canonical
 * copy from via6living.com/future-residents-club):
 *
 *   1. Intro        — centered "All the perks" heading + member intro.
 *   2. Benefits     — 3×2 grid of icon cards: Pre-Approved, First Choice,
 *                     Priority, Commitment Free, Get to Know Us,
 *                     Joining Is Easy.
 *   3. How It Works — left: heading, richtext steps, APPLY NOW CTA
 *                     (property_apply_now_url); right: property photo
 *                     (falls back to the best interior gallery shot).
 *
 * Weidner Classic styling: mustard icons and CTA (matching PetPolicy /
 * AmenitiesList), uppercase heading type, accent rule on How It Works.
 * Interior hero stays on HeroSolidBG in pages.json.
 *
 * Maintenance checklist when re-syncing with the other Weidner themes:
 *   1. Keep token keys (`futureresidentsclub.*`) aligned with the shared
 *      Weidner FutureResidentsClub so site overrides carry across themes.
 *   2. Keep defaults in contentSchema only (the runtime derives them).
 *   3. Run `pnpm run lint:schema` and `pnpm run typecheck`.
 */

import { useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  useGallery,
  EditableToken,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

const MUSTARD = 'var(--vibe-color-secondary)';

interface GalleryPhoto {
  image?: string | null;
  title?: string | null;
  alt_text?: string | null;
  description?: string | null;
  original_url?: string | null;
  source_page_urls?: string | null;
}

const INTERIOR_KEYWORDS = [
  'interior', 'apartment', 'kitchen', 'bedroom', 'bathroom',
  'living room', 'closet', 'unit', 'home', 'suite',
];

function photoText(p: GalleryPhoto): string {
  return [p.title, p.description, p.alt_text, p.image, p.original_url, p.source_page_urls]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

function findInteriorPhoto(gallery: GalleryPhoto[]): GalleryPhoto | null {
  let best: GalleryPhoto | null = null;
  let bestScore = -1;
  for (const p of gallery) {
    if (!p.image) continue;
    const text = photoText(p);
    const score = INTERIOR_KEYWORDS.reduce((s, kw) => s + (text.includes(kw) ? 1 : 0), 0);
    if (score > bestScore) {
      best = p;
      bestScore = score;
    }
  }
  return best || gallery.find((p) => p.image) || null;
}

const howItWorksImageDef: SlotGroupDefinition = {
  label: 'How It Works Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Apartment interior' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const ctaLinkDef: SlotGroupDefinition = {
  label: 'CTA Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_apply_now_url',
      suggestedFields: ['property_apply_now_url', 'property_website_url'],
    },
    label: { label: 'Label', type: 'text', defaultValue: 'APPLY NOW' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// Standard Weidner benefit icons
// ============================================================================

const ICON_PROPS = {
  viewBox: '0 0 48 48',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.5,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  className: 'frc-icon',
  'aria-hidden': true,
};

function IconPreApproved() {
  return (
    <svg {...ICON_PROPS}>
      <rect x="6" y="8" width="36" height="32" rx="2" />
      <path d="M6 16h36" />
      <path d="M16 24l4 4 8-8" />
    </svg>
  );
}

function IconFirstChoice() {
  return (
    <svg {...ICON_PROPS}>
      <rect x="8" y="4" width="32" height="40" rx="2" />
      <path d="M16 14h16" />
      <path d="M16 22h12" />
      <path d="M16 30h8" />
      <circle cx="32" cy="36" r="3" fill="currentColor" />
    </svg>
  );
}

function IconPriority() {
  return (
    <svg {...ICON_PROPS}>
      <path d="M24 6v36" />
      <path d="M14 12l10-6 10 6" />
      <path d="M14 20h20" />
      <path d="M18 28h12" />
      <path d="M20 36h8" />
    </svg>
  );
}

function IconCommitmentFree() {
  return (
    <svg {...ICON_PROPS}>
      <circle cx="24" cy="20" r="12" />
      <path d="M18 20l4 4 8-8" />
      <path d="M16 36c0-4 4-8 8-8s8 4 8 8" />
    </svg>
  );
}

function IconGetToKnow() {
  return (
    <svg {...ICON_PROPS}>
      <rect x="6" y="8" width="36" height="28" rx="2" />
      <path d="M6 16h36" />
      <path d="M14 24h6" />
      <path d="M14 30h10" />
      <path d="M28 24h6" />
      <path d="M28 30h6" />
      <line x1="24" y1="16" x2="24" y2="36" />
      <path d="M16 40h16" />
    </svg>
  );
}

function IconJoiningEasy() {
  return (
    <svg {...ICON_PROPS}>
      <circle cx="24" cy="16" r="8" />
      <path d="M12 40c0-6.627 5.373-12 12-12s12 5.373 12 12" />
      <path d="M30 14l2 2-2 2" />
    </svg>
  );
}

// ============================================================================
// Main component
// ============================================================================

export function FutureResidentsClub() {
  const title = useContentToken('futureresidentsclub.title');
  const subtitle = useContentToken('futureresidentsclub.subtitle');
  const howTitle = useContentToken('futureresidentsclub.how_title');

  const b1Title = useContentToken('futureresidentsclub.benefit1_title');
  const b1Desc = useContentToken('futureresidentsclub.benefit1_desc');
  const b2Title = useContentToken('futureresidentsclub.benefit2_title');
  const b2Desc = useContentToken('futureresidentsclub.benefit2_desc');
  const b3Title = useContentToken('futureresidentsclub.benefit3_title');
  const b3Desc = useContentToken('futureresidentsclub.benefit3_desc');
  const b4Title = useContentToken('futureresidentsclub.benefit4_title');
  const b4Desc = useContentToken('futureresidentsclub.benefit4_desc');
  const b5Title = useContentToken('futureresidentsclub.benefit5_title');
  const b5Desc = useContentToken('futureresidentsclub.benefit5_desc');
  const b6Title = useContentToken('futureresidentsclub.benefit6_title');
  const b6Desc = useContentToken('futureresidentsclub.benefit6_desc');

  const howImage = useSlotGroup('futureresidentsclub.how_image', howItWorksImageDef);
  const ctaLink = useSlotGroup('futureresidentsclub.cta_link', ctaLinkDef);

  const gallery = useGallery() as GalleryPhoto[];

  useRevealStyles();
  const headingRef = useScrollReveal<HTMLDivElement>();
  const gridRef = useScrollReveal<HTMLDivElement>();
  const howRef = useScrollReveal<HTMLDivElement>();

  const smartPhoto = useMemo(() => findInteriorPhoto(gallery), [gallery]);
  const imageSrc = howImage.image || smartPhoto?.image;
  const imageAlt = howImage.alt || smartPhoto?.alt_text || 'Apartment interior';

  return (
    <section className="frc-section">
      <div className="frc-inner">
        {/* Intro */}
        <div ref={headingRef} className="reveal-fade-up frc-intro">
          <EditableToken path="futureresidentsclub.title" as="h2" className="frc-title">
            {title}
          </EditableToken>
          <EditableToken path="futureresidentsclub.subtitle" as="p" className="frc-subtitle">
            {subtitle}
          </EditableToken>
        </div>

        {/* Benefits grid */}
        <div ref={gridRef} className="reveal-fade-up frc-grid">
          <div className="frc-card">
            <div className="frc-card-icon"><IconPreApproved /></div>
            <EditableToken path="futureresidentsclub.benefit1_title" as="h3" className="frc-card-title">{b1Title}</EditableToken>
            <EditableToken path="futureresidentsclub.benefit1_desc" as="p" className="frc-card-desc">{b1Desc}</EditableToken>
          </div>

          <div className="frc-card">
            <div className="frc-card-icon"><IconFirstChoice /></div>
            <EditableToken path="futureresidentsclub.benefit2_title" as="h3" className="frc-card-title">{b2Title}</EditableToken>
            <EditableToken path="futureresidentsclub.benefit2_desc" as="p" className="frc-card-desc">{b2Desc}</EditableToken>
          </div>

          <div className="frc-card">
            <div className="frc-card-icon"><IconPriority /></div>
            <EditableToken path="futureresidentsclub.benefit3_title" as="h3" className="frc-card-title">{b3Title}</EditableToken>
            <EditableToken path="futureresidentsclub.benefit3_desc" as="p" className="frc-card-desc">{b3Desc}</EditableToken>
          </div>

          <div className="frc-card">
            <div className="frc-card-icon"><IconCommitmentFree /></div>
            <EditableToken path="futureresidentsclub.benefit4_title" as="h3" className="frc-card-title">{b4Title}</EditableToken>
            <EditableToken path="futureresidentsclub.benefit4_desc" as="p" className="frc-card-desc">{b4Desc}</EditableToken>
          </div>

          <div className="frc-card">
            <div className="frc-card-icon"><IconGetToKnow /></div>
            <EditableToken path="futureresidentsclub.benefit5_title" as="h3" className="frc-card-title">{b5Title}</EditableToken>
            <EditableToken path="futureresidentsclub.benefit5_desc" as="p" className="frc-card-desc">{b5Desc}</EditableToken>
          </div>

          <div className="frc-card">
            <div className="frc-card-icon"><IconJoiningEasy /></div>
            <EditableToken path="futureresidentsclub.benefit6_title" as="h3" className="frc-card-title">{b6Title}</EditableToken>
            <EditableToken path="futureresidentsclub.benefit6_desc" as="p" className="frc-card-desc">{b6Desc}</EditableToken>
          </div>
        </div>

        {/* How It Works */}
        <div ref={howRef} className="reveal-fade-up frc-how">
          <div className="frc-how-text">
            <EditableToken path="futureresidentsclub.how_title" as="h3" className="frc-how-title">
              {howTitle}
            </EditableToken>
            <RichTextContent
              path="futureresidentsclub.how_body"
              label="How It Works"
              as="div"
              className="frc-how-body"
            />
            <div className="frc-cta-wrap">
              <EditableSlotGroup groupPath="futureresidentsclub.cta_link" definition={ctaLinkDef}>
                <a
                  href={ctaLink.url || '#'}
                  target={ctaLink._target || '_self'}
                  rel={ctaLink._target === '_blank' ? 'noopener noreferrer' : undefined}
                  className="frc-cta"
                >
                  <span>{ctaLink.label || 'APPLY NOW'}</span>
                  <svg
                    className="frc-cta__icon"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M5 12h14M13 6l6 6-6 6"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </a>
              </EditableSlotGroup>
            </div>
          </div>

          <EditableSlotGroup groupPath="futureresidentsclub.how_image" definition={howItWorksImageDef}>
            <div className="frc-how-photo">
              {imageSrc ? (
                <VibesitesImage
                  url={imageSrc}
                  alt={imageAlt}
                  sizes="(min-width: 768px) 520px, 92vw"
                  className="frc-how-img"
                />
              ) : (
                <div className="frc-how-placeholder" />
              )}
            </div>
          </EditableSlotGroup>
        </div>
      </div>

      <style>{`
        .frc-section {
          padding: 3rem 0;
          background: var(--vibe-color-background, #fff);
        }
        @media (min-width: 768px) {
          .frc-section { padding: 5rem 0; }
        }
        .frc-inner {
          max-width: 72rem;
          margin: 0 auto;
          padding: 0 1rem;
        }
        @media (min-width: 640px) {
          .frc-inner { padding: 0 1.5rem; }
        }
        @media (min-width: 1024px) {
          .frc-inner { padding: 0 2rem; }
        }

        .frc-intro {
          text-align: center;
          margin-bottom: 3.5rem;
        }
        .frc-title {
          margin: 0 0 1.25rem;
          font-family: var(--vibe-font-heading);
          font-size: 1.875rem;
          font-weight: 500;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          color: #000;
        }
        @media (min-width: 640px) {
          .frc-title { font-size: 2.25rem; }
        }
        .frc-subtitle {
          max-width: 48rem;
          margin: 0 auto;
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          line-height: 1.75;
          color: var(--vibe-color-text-muted);
        }

        .frc-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2rem;
          margin-bottom: 4rem;
        }
        @media (min-width: 769px) {
          .frc-grid { grid-template-columns: repeat(2, 1fr); gap: 2.5rem 2rem; }
        }
        @media (min-width: 1025px) {
          .frc-grid { grid-template-columns: repeat(3, 1fr); }
        }

        .frc-card {
          text-align: center;
          padding: 1rem 0.5rem;
        }
        .frc-card-icon {
          display: flex;
          justify-content: center;
          margin-bottom: 1rem;
          color: ${MUSTARD};
        }
        .frc-icon {
          width: 2.75rem;
          height: 2.75rem;
        }
        .frc-card-title {
          margin: 0 0 0.6rem;
          font-family: var(--vibe-font-heading);
          font-size: 1rem;
          font-weight: 600;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: var(--vibe-color-text);
        }
        .frc-card-desc {
          margin: 0;
          font-family: var(--vibe-font-body);
          font-size: 0.875rem;
          line-height: 1.65;
          color: var(--vibe-color-text-muted);
        }

        .frc-how {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2.5rem;
          align-items: start;
          padding-top: 2.5rem;
          border-top: 1px solid #e8e8e8;
        }
        @media (min-width: 769px) {
          .frc-how { grid-template-columns: 1fr 1fr; gap: 3rem; }
        }

        .frc-how-text {
          padding: 0.25rem 0 0 1.25rem;
          border-left: 4px solid ${MUSTARD};
        }
        .frc-how-title {
          margin: 0 0 1.25rem;
          font-family: var(--vibe-font-heading);
          font-size: 1.5rem;
          font-weight: 500;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          color: #000;
        }
        @media (min-width: 640px) {
          .frc-how-title { font-size: 1.875rem; }
        }
        .frc-how-body {
          font-family: var(--vibe-font-body);
          font-size: 0.9375rem;
          line-height: 1.75;
          color: var(--vibe-color-text);
        }
        .frc-how-body p { margin: 0 0 1rem; }
        .frc-how-body ul {
          list-style: disc;
          padding-left: 1.25rem;
          margin: 0 0 1rem;
        }
        .frc-how-body li { margin-bottom: 0.5rem; }
        .frc-how-body li::marker { color: ${MUSTARD}; }
        .frc-how-body strong { font-weight: 600; }
        .frc-how-body a {
          color: var(--vibe-color-primary);
          text-decoration: underline;
        }

        .frc-cta-wrap { margin-top: 1.5rem; }
        .frc-cta {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.65rem;
          min-width: 14rem;
          height: 56px;
          padding: 0 1.75rem;
          background: ${MUSTARD};
          color: var(--vibe-color-text-on-secondary);
          font-family: var(--vibe-font-body);
          font-size: 0.9rem;
          font-weight: 700;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          text-decoration: none;
          border: none;
          border-radius: 0;
          transition: background 0.25s ease, color 0.25s ease;
        }
        .frc-cta:hover {
          background: #b6b6b6;
          color: #000;
        }
        .frc-cta__icon { flex-shrink: 0; }

        .frc-how-photo {
          width: 100%;
          aspect-ratio: 4 / 3;
          overflow: hidden;
          background: #f7f7f7;
        }
        .frc-how-img {
          display: block;
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.5s ease;
        }
        .frc-how-photo:hover .frc-how-img { transform: scale(1.03); }
        .frc-how-placeholder {
          width: 100%;
          height: 100%;
        }

        @media (prefers-reduced-motion: reduce) {
          .frc-cta,
          .frc-how-img { transition: none; }
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'FutureResidentsClub',
  displayName: 'Future Residents Club',
  tokens: {
    'futureresidentsclub.title': {
      label: 'Title',
      type: 'text',
      defaultValue: 'All the perks',
    },
    'futureresidentsclub.subtitle': {
      label: 'Subtitle',
      type: 'text',
      defaultValue:
        "When moving, you have a vast array of choices to make, let us help you make choosing your new home easier. With the Future Resident's Club, start creating your sense of community before ever moving into your new home! Here's what you get for being a member:",
    },
    'futureresidentsclub.how_title': {
      label: 'How It Works Title',
      type: 'text',
      defaultValue: 'How It Works',
    },
    'futureresidentsclub.how_body': {
      label: 'How It Works Body',
      type: 'richtext',
      defaultValue:
        "<p>When an apartment becomes available, the community will contact our Club members, in order of application submission. You will have 72 hours from the time we contact you to come by and tour the actual apartment.</p><p><strong>Once you've seen it, you have 24 hours to:</strong></p><ul><li><strong>Select the unit</strong> (your Future Residents Club deposit is then applied to your apartment deposit).</li><li><strong>Cancel altogether</strong> (your Future Residents Club deposit is refunded).</li><li><strong>Stay on Future Residents Club list to select another apartment</strong> (however, you will be moved to the bottom of the list).</li></ul>",
    },
    'futureresidentsclub.benefit1_title': { label: 'Pre-Approved Title', type: 'text', defaultValue: 'Pre-Approved' },
    'futureresidentsclub.benefit1_desc': { label: 'Pre-Approved Description', type: 'text', defaultValue: 'Run your application now, and get pre-approved for move-in (on approved credit/non-refundable app fee).' },
    'futureresidentsclub.benefit2_title': { label: 'First Choice Title', type: 'text', defaultValue: 'First Choice' },
    'futureresidentsclub.benefit2_desc': { label: 'First Choice Description', type: 'text', defaultValue: 'As apartments become available for move-in, you get first choice of your new home before the apartments are offered to the market.' },
    'futureresidentsclub.benefit3_title': { label: 'Priority Title', type: 'text', defaultValue: 'Priority' },
    'futureresidentsclub.benefit3_desc': { label: 'Priority Description', type: 'text', defaultValue: 'Receive first choice and flexibility of the time-frame that you want on move-in day.' },
    'futureresidentsclub.benefit4_title': { label: 'Commitment Free Title', type: 'text', defaultValue: 'Commitment Free' },
    'futureresidentsclub.benefit4_desc': { label: 'Commitment Free Description', type: 'text', defaultValue: "Your deposit for the Future Resident's Club is fully refundable until we have available apartment homes to view and you make your new home at one of our choice communities." },
    'futureresidentsclub.benefit5_title': { label: 'Get to Know Us Title', type: 'text', defaultValue: 'Get to Know Us' },
    'futureresidentsclub.benefit5_desc': { label: 'Get to Know Us Description', type: 'text', defaultValue: 'You will be automatically signed up for any community correspondence and will be invited to any events. A great way to get to know your neighbors before moving in.' },
    'futureresidentsclub.benefit6_title': { label: 'Joining Is Easy Title', type: 'text', defaultValue: 'Joining Is Easy' },
    'futureresidentsclub.benefit6_desc': { label: 'Joining Is Easy Description', type: 'text', defaultValue: 'Simply fill out an online application by selecting your desired floor plan, select a "wait list" unit, pay a holding deposit, and we\'ll hold your spot in line. Easy!' },
  },
  slotGroups: {
    'futureresidentsclub.how_image': {
      label: 'How It Works Image',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
        alt: { label: 'Alt Text', type: 'text', defaultValue: 'Apartment interior' },
      },
      sourceBinding: {
        collection: 'photo_gallery',
        defaultIndex: 0,
        fieldMappings: { image: 'image', alt: 'alt_text' },
      },
    },
    'futureresidentsclub.cta_link': {
      label: 'CTA Link',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultField: 'property_apply_now_url',
          suggestedFields: ['property_apply_now_url', 'property_website_url'],
        },
        label: { label: 'Label', type: 'text', defaultValue: 'APPLY NOW' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
  },
};

(FutureResidentsClub as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default FutureResidentsClub;
